import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  PersonSimpleRun,
  PersonSimpleWalk,
  Barbell,
  Scales,
  Bicycle,
  TrendUp,
  Heartbeat,
  Lightning,
  Clock,
  Fire,
  Note,
  CalendarBlank
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const MACHINE_CONFIGS = {
  treadmill: {
    label: 'Treadmill',
    Icon: PersonSimpleRun,
    fields: [
      { key: 'distance', label: 'Distance', unit: 'mi', type: 'number', step: '0.1', placeholder: '2.5' },
      { key: 'speed', label: 'Speed', unit: 'mph', type: 'number', step: '0.1', placeholder: '6.0' },
      { key: 'incline', label: 'Incline', unit: '%', type: 'number', step: '0.5', placeholder: '3.0' },
    ]
  },
  elliptical: {
    label: 'Elliptical',
    Icon: Heartbeat,
    fields: [
      { key: 'distance', label: 'Distance', unit: 'mi', type: 'number', step: '0.1', placeholder: '3.0' },
      { key: 'resistance', label: 'Resistance', unit: 'lvl', type: 'number', placeholder: '8' },
      { key: 'strides_per_min', label: 'Strides/Min', unit: 'spm', type: 'number', placeholder: '140' },
    ]
  },
  stair_climber: {
    label: 'Stair Climber',
    Icon: TrendUp,
    fields: [
      { key: 'floors', label: 'Floors', unit: 'flrs', type: 'number', placeholder: '50' },
      { key: 'level', label: 'Level', unit: 'lvl', type: 'number', placeholder: '7' },
    ]
  },
  row_machine: {
    label: 'Row Machine',
    Icon: Barbell,
    fields: [
      { key: 'distance', label: 'Distance', unit: 'm', type: 'number', placeholder: '2000' },
      { key: 'strokes_per_min', label: 'Strokes/Min', unit: 'spm', type: 'number', placeholder: '28' },
      { key: 'resistance', label: 'Resistance', unit: 'lvl', type: 'number', placeholder: '6' },
    ]
  },
  running: {
    label: 'Running',
    Icon: Lightning,
    fields: [
      { key: 'distance', label: 'Distance', unit: 'mi', type: 'number', step: '0.1', placeholder: '3.0' },
      { key: 'pace', label: 'Pace', unit: 'min/mi', type: 'text', placeholder: '8:30' },
    ]
  },
  speed_walking: {
    label: 'Speed Walking',
    Icon: PersonSimpleWalk,
    fields: [
      { key: 'distance', label: 'Distance', unit: 'mi', type: 'number', step: '0.1', placeholder: '2.0' },
      { key: 'pace', label: 'Pace', unit: 'min/mi', type: 'text', placeholder: '15:00' },
    ]
  },
  stationary_bike: {
    label: 'Stationary Bike',
    Icon: Bicycle,
    fields: [
      { key: 'distance', label: 'Distance', unit: 'mi', type: 'number', step: '0.1', placeholder: '8.0' },
      { key: 'resistance', label: 'Resistance', unit: 'lvl', type: 'number', placeholder: '10' },
      { key: 'rpm', label: 'RPM', unit: 'rpm', type: 'number', placeholder: '80' },
    ]
  },
  weekly_weight: {
    label: 'Weekly Weight',
    Icon: Scales,
    isWeightTracker: true,
    fields: [
      { key: 'weight', label: 'Weight', unit: 'lbs', type: 'number', step: '0.1', placeholder: '165.0' },
    ]
  },
};

function calcTotalMinutes(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff < 0) diff += 24 * 60;
  return diff;
}

const TrackerPage = () => {
  const { machineType } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const config = MACHINE_CONFIGS[machineType];

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    calories_burned: '',
    notes: '',
  });

  // Autofill from last log
  useEffect(() => {
    if (!config || !token) return;
    const fetchLastLog = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/last-log/${machineType}`, { withCredentials: true, headers });
        if (res.data && Object.keys(res.data).length > 0) {
          const last = res.data;
          const prefill = { date: new Date().toISOString().split('T')[0] };
          config.fields.forEach(f => {
            if (last[f.key] !== undefined && last[f.key] !== null) {
              prefill[f.key] = String(last[f.key]);
            }
          });
          if (last.calories_burned) prefill.calories_burned = String(last.calories_burned);
          setFormData(prev => ({ ...prev, ...prefill }));
        }
      } catch {
        // Silently fail - autofill is optional
      }
    };
    fetchLastLog();
  }, [machineType, token, config]);

  const totalMinutes = useMemo(
    () => calcTotalMinutes(formData.start_time, formData.end_time),
    [formData.start_time, formData.end_time]
  );

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground text-lg mb-4">Unknown tracker type</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#D4AF37] text-black rounded-full">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const MachineIcon = config.Icon;
  const isWeight = config.isWeightTracker;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isWeight) {
      if (!formData.weight) {
        toast.error('Please enter your weight');
        return;
      }
    } else {
      if (!formData.start_time || !formData.end_time) {
        toast.error('Please enter start and end time');
        return;
      }
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const reqConfig = { withCredentials: true, headers };

      if (isWeight) {
        await axios.post(`${API}/weight-logs`, {
          weight: parseFloat(formData.weight),
          notes: formData.notes || null,
          date: formData.date,
        }, reqConfig);
      } else {
        const payload = {
          cardio_type: machineType,
          date: formData.date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          duration_minutes: totalMinutes || 0,
          calories_burned: formData.calories_burned ? parseInt(formData.calories_burned) : null,
          notes: formData.notes || null,
        };

        config.fields.forEach(f => {
          if (formData[f.key] !== undefined && formData[f.key] !== '') {
            payload[f.key] = f.type === 'number' ? parseFloat(formData[f.key]) : formData[f.key];
          }
        });

        await axios.post(`${API}/cardio-logs`, payload, reqConfig);
      }

      toast.success(`${config.label} logged!`);
      navigate('/session-complete', { state: { log: { ...formData, cardio_type: machineType, total_minutes: totalMinutes } } });
    } catch (error) {
      toast.error('Failed to save. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8 relative overflow-hidden" data-testid="tracker-page">
      {/* Watermark */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url(${LOGO_URL})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '50%',
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-card flex items-center justify-center border border-border"
            data-testid="tracker-back-btn"
          >
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <MachineIcon weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {config.label}
              </h1>
              <p className="text-xs text-muted-foreground">
                {isWeight ? 'Log your weight' : 'Log your session'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-6 py-6 space-y-5 relative z-10">
        {/* Date */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
            <CalendarBlank weight="duotone" className="w-4 h-4" />
            Date
          </Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => updateForm('date', e.target.value)}
            className="bg-background border-border rounded-xl"
            data-testid="tracker-date-input"
          />
        </div>

        {/* Start/End Time (not for weight) */}
        {!isWeight && (
          <div className="premium-card p-4">
            <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
              <Clock weight="duotone" className="w-4 h-4" />
              Time
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Start Time</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => updateForm('start_time', e.target.value)}
                  className="bg-background border-border rounded-xl"
                  data-testid="tracker-start-time"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => updateForm('end_time', e.target.value)}
                  className="bg-background border-border rounded-xl"
                  data-testid="tracker-end-time"
                />
              </div>
            </div>
            {totalMinutes !== null && totalMinutes > 0 && (
              <div className="mt-3 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#D4AF37]/5 border border-[#D4AF37]/10">
                <Clock weight="fill" className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-sm font-bold text-[#D4AF37]" data-testid="total-time-display">
                  Total: {totalMinutes} min
                </span>
              </div>
            )}
          </div>
        )}

        {/* Machine-Specific Fields */}
        {config.fields.length > 0 && (
          <div className="premium-card p-4">
            <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 block">
              {isWeight ? 'Weight Entry' : 'Session Details'}
            </Label>
            <div className={`grid ${config.fields.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              {config.fields.map((field) => (
                <div key={field.key}>
                  <Label className="text-xs text-muted-foreground mb-1 block">{field.label}</Label>
                  <div className="relative">
                    <Input
                      type={field.type}
                      step={field.step}
                      value={formData[field.key] || ''}
                      onChange={(e) => updateForm(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="bg-background border-border rounded-xl pr-14"
                      data-testid={`tracker-${field.key}-input`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                      {field.unit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calories (not for weight) */}
        {!isWeight && (
          <div className="premium-card p-4">
            <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
              <Fire weight="duotone" className="w-4 h-4" />
              Calories Burned
            </Label>
            <div className="relative">
              <Input
                type="number"
                value={formData.calories_burned}
                onChange={(e) => updateForm('calories_burned', e.target.value)}
                placeholder="250"
                className="bg-background border-border rounded-xl pr-14"
                data-testid="tracker-calories-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">cal</span>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
            <Note weight="duotone" className="w-4 h-4" />
            Notes
          </Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => updateForm('notes', e.target.value)}
            placeholder="Any notes about this session..."
            className="bg-background border-border rounded-xl min-h-[80px] resize-none"
            data-testid="tracker-notes-input"
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover"
          data-testid="tracker-save-btn"
        >
          {loading ? 'Saving...' : `Save ${config.label}`}
        </Button>
      </form>
    </div>
  );
};

export default TrackerPage;
